import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FiShoppingCart, FiEye, FiSearch, FiDownload } from 'react-icons/fi';
import AnimatedCounter from '../components/AnimatedCounter';

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const { selectedHierarchy } = useAuth();

  useEffect(() => {
    // Subscribe to real-time updates
    const unsubscribe = subscribeToOrders();
    
    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [selectedHierarchy]);

  const subscribeToOrders = () => {
    if (!selectedHierarchy?.outlet?.id) {
      setLoading(false);
      return null;
    }
    
    setLoading(true);
    
    const ordersRef = collection(db, 'orders');
    
    // Try with outletId filter and createdAt ordering
    try {
      const q = query(
        ordersRef,
        where('outletId', '==', selectedHierarchy.outlet.id),
        orderBy('createdAt', 'desc'),
        limit(100)
      );
      
      // Subscribe to real-time updates
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const ordersData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              // Note: createdAt from Firebase is in UTC - preserved as-is
              total: data.totalAmount || data.total || 0,
            };
          });
          
          setOrders(ordersData);
          setLoading(false);
        },
        (error) => {
          // Fallback: Try without orderBy if index is missing
          const fallbackQuery = query(
            ordersRef,
            where('outletId', '==', selectedHierarchy.outlet.id),
            limit(100)
          );
          
          return onSnapshot(
            fallbackQuery,
            (snapshot) => {
              const ordersData = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  id: doc.id,
                  ...data,
                  // Note: createdAt from Firebase is in UTC - preserved as-is
                  total: data.totalAmount || data.total || 0,
                };
              });
              
              setOrders(ordersData);
              setLoading(false);
            },
            (fallbackError) => {
              setLoading(false);
            }
          );
        }
      );
      
      return unsubscribe;
    } catch (error) {
      setLoading(false);
      return null;
    }
  };

  const filteredOrders = orders.filter(order =>
    order.numericId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customerId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customerPhone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredOrders.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'paid':
        return 'success';
      case 'in_progress':
      case 'in-progress':
        return 'info';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'danger';
      case 'void':
      case 'voided':
        return 'dark';
      default:
        return 'primary';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      let date;
      
      // Handle Firestore Timestamp (already in UTC)
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Handle Date object
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Handle ISO string (UTC format like "2025-11-06T10:30:00.000Z")
      else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      }
      // Handle timestamp number (milliseconds since epoch)
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      }
      else {
        return '-';
      }
      
      // Validate date
      if (!date || isNaN(date.getTime())) {
        return '-';
      }
      
      // Format using UTC to avoid timezone conversion
      // This ensures the date displayed matches the date stored in Firebase
      const year = date.getUTCFullYear();
      const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
      const day = date.getUTCDate();
      
      return `${month} ${day}, ${year}`;
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return '-';
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    try {
      let date;
      
      // Handle Firestore Timestamp
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Handle Date object
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Handle ISO string
      else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      }
      // Handle timestamp number
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      }
      else {
        return '-';
      }
      
      // Validate date
      if (!date || isNaN(date.getTime())) {
        return '-';
      }
      
      // Format with UTC time
      const year = date.getUTCFullYear();
      const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
      const day = date.getUTCDate();
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      
      return `${month} ${day}, ${year} ${hours}:${minutes} UTC`;
    } catch (error) {
      console.error('Error formatting datetime:', error, timestamp);
      return '-';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return `$${Number(amount).toFixed(2)}`;
  };

  // Calculate dynamic order statistics
  const calculateStats = () => {
    const stats = {
      total: orders.length,
      completed: 0,
      inProgress: 0,
      pending: 0,
      cancelled: 0,
      totalAmount: 0,
      completedAmount: 0,
      averageAmount: 0,
      statusBreakdown: {}
    };

    orders.forEach(ord => {
      const amount = Number(ord.total) || 0;
      const status = (ord.status || 'completed').toLowerCase();
      
      // Count by status
      stats.statusBreakdown[status] = (stats.statusBreakdown[status] || 0) + 1;
      
      // Track specific statuses
      if (status === 'completed') stats.completed++;
      if (status === 'in_progress' || status === 'in-progress') stats.inProgress++;
      if (status === 'pending') stats.pending++;
      if (status === 'cancelled') stats.cancelled++;
      
      // Calculate amounts (exclude cancelled orders from totals)
      if (status !== 'cancelled' && status !== 'void' && status !== 'voided') {
        stats.totalAmount += amount;
        if (status === 'completed' || status === 'paid') {
          stats.completedAmount += amount;
        }
      }
    });

    // Calculate average
    const nonCancelledCount = stats.total - stats.cancelled;
    stats.averageAmount = nonCancelledCount > 0 ? stats.totalAmount / nonCancelledCount : 0;

    return stats;
  };

  const stats = calculateStats();

  return (
    <div className="container-fluid">
      <div className="page-header mb-4">
        <h1 className="page-title">Orders</h1>
        <p className="page-subtitle">View and manage customer orders</p>
      </div>

      {/* Stats Cards - Dynamically Generated */}
      <div className="row g-3 mb-4">
        {/* Total Orders */}
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Total Orders</p>
                  <h3 className="mb-0">
                    <AnimatedCounter value={stats.total} />
                  </h3>
                  <small className="text-muted">
                    <AnimatedCounter 
                      value={stats.totalAmount} 
                      decimals={2} 
                      prefix="$"
                    />
                    {stats.cancelled > 0 && (
                      <>
                        {' '}(<AnimatedCounter value={stats.cancelled} /> cancelled)
                      </>
                    )}
                  </small>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--primary-gradient)', width: '48px', height: '48px' }}>
                  <FiShoppingCart className="text-white" size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Completed Orders */}
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Completed</p>
                  <h3 className="mb-0 text-success">
                    <AnimatedCounter value={stats.completed} />
                  </h3>
                  <small className="text-muted">
                    <AnimatedCounter 
                      value={stats.completedAmount} 
                      decimals={2} 
                      prefix="$"
                    />
                  </small>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--success-gradient)', width: '48px', height: '48px' }}>
                  <i className="bi bi-check-circle text-white" style={{ fontSize: '24px' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* In Progress */}
        {stats.inProgress > 0 && (
          <div className="col-md-3">
            <div className="card hover-lift">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-1 small">In Progress</p>
                    <h3 className="mb-0 text-info">
                      <AnimatedCounter value={stats.inProgress} />
                    </h3>
                    <small className="text-muted">
                      <AnimatedCounter 
                        value={orders
                          .filter(ord => ord.status?.toLowerCase() === 'in_progress' || ord.status?.toLowerCase() === 'in-progress')
                          .reduce((sum, ord) => sum + (Number(ord.total) || 0), 0)}
                        decimals={2}
                        prefix="$"
                      />
                    </small>
                  </div>
                  <div className="stat-card-icon" style={{ background: 'var(--info-gradient)', width: '48px', height: '48px' }}>
                    <i className="bi bi-hourglass-split text-white" style={{ fontSize: '24px' }}></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pending */}
        {stats.pending > 0 && (
          <div className="col-md-3">
            <div className="card hover-lift">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-1 small">Pending</p>
                    <h3 className="mb-0 text-warning">
                      <AnimatedCounter value={stats.pending} />
                    </h3>
                    <small className="text-muted">
                      <AnimatedCounter 
                        value={orders
                          .filter(ord => ord.status?.toLowerCase() === 'pending')
                          .reduce((sum, ord) => sum + (Number(ord.total) || 0), 0)}
                        decimals={2}
                        prefix="$"
                      />
                    </small>
                  </div>
                  <div className="stat-card-icon" style={{ background: 'var(--warning-gradient)', width: '48px', height: '48px' }}>
                    <i className="bi bi-clock text-white" style={{ fontSize: '24px' }}></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Average Order Value */}
        {(stats.inProgress === 0 || stats.pending === 0) && (
          <div className="col-md-3">
            <div className="card hover-lift">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-1 small">Average Order</p>
                    <h3 className="mb-0">
                      <AnimatedCounter 
                        value={stats.averageAmount} 
                        decimals={2} 
                        prefix="$"
                      />
                    </h3>
                    <small className="text-muted">
                      <AnimatedCounter value={stats.total - stats.cancelled} /> orders
                    </small>
                  </div>
                  <div className="stat-card-icon" style={{ background: 'var(--accent-gradient)', width: '48px', height: '48px' }}>
                    <i className="bi bi-graph-up text-white" style={{ fontSize: '24px' }}></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Orders Table */}
      <div className="card hover-lift">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Orders</h5>
          <div>
            <button className="btn btn-sm btn-outline-primary me-2">
              <i className="bi bi-funnel me-2"></i>
              Filter
            </button>
            <button className="btn btn-sm btn-primary">
              <i className="bi bi-plus-circle me-2"></i>
              New Order
            </button>
          </div>
        </div>
        
        {/* Filter Controls */}
        {!loading && orders.length > 0 && (
          <div className="card-body border-bottom">
            <div className="row g-3 align-items-end">
              <div className="col-md-12">
                <label className="form-label small text-muted mb-1">Search</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search by order number, customer name, phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            {searchTerm && (
              <div className="mt-2">
                <small className="text-muted">
                  Showing {filteredOrders.length} of {orders.length} orders
                </small>
              </div>
            )}
          </div>
        )}

        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading orders...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-5">
              <FiShoppingCart size={48} className="text-muted mb-3" />
              <p className="text-muted mb-0">
                {searchTerm ? 'No orders match your search.' : 'No orders found.'}
              </p>
              {searchTerm && (
                <button
                  className="btn btn-sm btn-outline-primary mt-2"
                  onClick={() => setSearchTerm('')}
                >
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Phone</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedOrders.map(order => (
                    <tr key={order.id}>
                      <td className="fw-semibold">
                        {order.numericId || `#${order.id.slice(0, 8)}`}
                      </td>
                      <td>{order.customerName || 'Walk-in Customer'}</td>
                      <td>{order.customerPhone || '-'}</td>
                      <td>{formatDate(order.createdAt)}</td>
                      <td>
                        <span className="badge bg-secondary text-capitalize">
                          {order.orderType || order.customerType || 'dine-in'}
                        </span>
                      </td>
                      <td className="fw-semibold">{formatCurrency(order.total)}</td>
                      <td>
                        <span className={`badge bg-${getStatusColor(order.status)}`}>
                          {order.status || 'completed'}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-sm btn-outline-primary me-2" title="View Order">
                          <FiEye />
                        </button>
                        <button className="btn btn-sm btn-outline-secondary" title="Download Receipt">
                          <FiDownload />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination Info and Status Breakdown */}
      {filteredOrders.length > 0 && (
        <>
          <div className="row mt-3 align-items-center">
            <div className="col-md-4">
              <div className="text-muted small">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredOrders.length)} of {filteredOrders.length} orders
                {searchTerm && ` (filtered from ${orders.length})`}
              </div>
            </div>
            <div className="col-md-4 text-center">
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <nav aria-label="Order pagination">
                  <ul className="pagination pagination-sm justify-content-center mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    
                    {/* First page */}
                    {currentPage > 3 && (
                      <>
                        <li className="page-item">
                          <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
                        </li>
                        {currentPage > 4 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                      </>
                    )}
                    
                    {/* Pages around current */}
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => page >= currentPage - 2 && page <= currentPage + 2)
                      .map(page => (
                        <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(page)}>
                            {page}
                          </button>
                        </li>
                      ))}
                    
                    {/* Last page */}
                    {currentPage < totalPages - 2 && (
                      <>
                        {currentPage < totalPages - 3 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                        <li className="page-item">
                          <button className="page-link" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
                        </li>
                      </>
                    )}
                    
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
            <div className="col-md-4 text-end">
              <div className="d-inline-flex gap-3 text-muted small">
                {Object.entries(stats.statusBreakdown).map(([status, count]) => (
                  <span key={status}>
                    <span className={`badge bg-${getStatusColor(status)} me-1`} style={{ fontSize: '0.65rem' }}>
                      {count}
                    </span>
                    <span className="text-capitalize">{status.replace('_', ' ')}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
